import { useTranslate } from "@similarweb/i18n-provider";
import { TextAnchorCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { useUserTeamMembership } from "../../hooks/useUserTeamMembership";
import { useTeamsTrackingService } from "../../hooks/useTeamsTrackingService";
import type { TeamListItem } from "../../types/teams.types";

import { navigateOnPlainClick } from "./navigateOnPlainClick";

type TeamsTableNameCellProps = {
    team: TeamListItem;
};

export const TeamsTableNameCell = ({ team }: TeamsTableNameCellProps) => {
    const { translate } = useTranslate();
    const tracking = useTeamsTrackingService();

    // Membership is read per cell rather than passed down as a column prop: the table captures its
    // column render closures on mount, so a set that resolves after the query settles never reaches
    // the cells. The query itself is shared through the react-query cache.
    const { userTeamIds } = useUserTeamMembership();

    return (
        <Flex
            alignItems="center"
            gap="8px"
        >
            <TextAnchorCell
                href={siNavigator.createTeamDetailLink({ id: team.teamId })}
                onClick={(event) => {
                    tracking.trackOpenTeamDetail(team.name ?? "", "name");
                    navigateOnPlainClick(event, () => {
                        void siNavigator.goToTeamDetailPage({ id: team.teamId });
                    });
                }}
                dataAutomation={`teams-table-name-${team.teamId}`}
            >
                {team.name ?? ""}
            </TextAnchorCell>
            {userTeamIds.has(team.teamId) ? (
                <Box
                    flex="0 0 auto"
                    data-automation={`teams-table-my-team-badge-${team.teamId}`}
                >
                    <Badge
                        bgColor={colorsPalettes.midnight["300"]}
                        color={colorsPalettes.carbon["0"]}
                        type="custom"
                    >
                        {translate("si.management.teams.card.badge_my_team")}
                    </Badge>
                </Box>
            ) : null}
        </Flex>
    );
};
